import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Avatar } from '@/components/ui/avatar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { 
  Globe, 
  Search, 
  ExternalLink,
  CheckCircle2,
  Circle,
  Filter,
  AlertCircle
} from 'lucide-react'
import { instagramService } from '@/services'
import type { SavedAnalyticsProfile } from '@/types'

interface GlobalAccount {
  id: string
  username: string
  fullName: string
  followers: number
  category: string
  url: string
}

interface GlobalAccountsListProps {
  selectedAccounts: string[]
  onAccountToggle: (accountId: string) => void
}

 

export function GlobalAccountsList({ selectedAccounts, onAccountToggle }: GlobalAccountsListProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [isLoading, setIsLoading] = useState(false)
  const [globalAccounts, setGlobalAccounts] = useState<GlobalAccount[]>([])
  const [error, setError] = useState<string | null>(null)
  const [categories, setCategories] = useState<string[]>(['All'])

  // Transform API response to GlobalAccount format
  const transformApiData = (profiles: SavedAnalyticsProfile[]): GlobalAccount[] => {
    return profiles
      .filter(profile => profile.response_code === 200 && profile.target_username && profile.profile_overview)
      .map(profile => ({
        id: profile.id,
        username: profile.target_username!,
        fullName: profile.profile_overview?.full_name || profile.target_username!,
        followers: profile.profile_overview?.followers || 0,
        category: profile.category || 'Other',
        url: `https://instagram.com/${profile.target_username}`
      }))
  }

  const fetchGlobalAccounts = async (force = false) => {
    if (!force && globalAccounts.length > 0) return // Already fetched, unless forced
    
    setIsLoading(true)
    setError(null)
    
    try {
      const response = await instagramService.getGlobalAccounts()
      if (response.success && response.profiles) {
        const transformedAccounts = transformApiData(response.profiles)
        setGlobalAccounts(transformedAccounts)
        
        // Extract unique categories
        const uniqueCategories = ['All', ...new Set(transformedAccounts.map(acc => acc.category).filter(Boolean))]
        setCategories(uniqueCategories)
      } else {
        setError('Failed to load global accounts')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load global accounts')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRefresh = () => {
    fetchGlobalAccounts(true)
  }

  const handleRetry = () => {
    fetchGlobalAccounts(true)
  }

  useEffect(() => {
    fetchGlobalAccounts()
  }, [])
  
  const filteredAccounts = globalAccounts
    .filter(account => {
      const matchesSearch = account.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           account.fullName.toLowerCase().includes(searchQuery.toLowerCase())
      const matchesCategory = selectedCategory === 'All' || account.category === selectedCategory
      return matchesSearch && matchesCategory
    })
    .sort((a, b) => b.followers - a.followers) // Sort by followers (highest first)

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Globe className="h-4 w-4" />
          <span>Loading global accounts...</span>
        </div>
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 animate-pulse">
            <div className="w-10 h-10 rounded-full bg-muted" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-muted rounded w-1/3" />
              <div className="h-3 bg-muted rounded w-2/3" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center p-6 border rounded-lg bg-destructive/5">
          <div className="text-center space-y-3">
            <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
            <div>
              <h3 className="font-semibold text-destructive">Failed to load global accounts</h3>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
            <Button onClick={handleRetry} variant="outline" size="sm">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (globalAccounts.length === 0 && !isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center p-6 border rounded-lg">
          <div className="text-center space-y-3">
            <Globe className="h-8 w-8 text-muted-foreground mx-auto" />
            <div>
              <h3 className="font-semibold">No global accounts available</h3>
              <p className="text-sm text-muted-foreground">Global accounts will appear here once they're added to the system.</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Globe className="h-4 w-4" />
          <span>{globalAccounts.length} global accounts available</span>
        </div>
        <Button 
          onClick={handleRefresh} 
          variant="ghost" 
          size="sm" 
          disabled={isLoading}
        >
          Refresh
        </Button>
      </div>
      
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search global accounts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-[140px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categories.map(category => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {filteredAccounts.map((account) => {
          const isSelected = selectedAccounts.includes(account.id)
          
          return (
            <div
              key={account.id}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                isSelected 
                  ? 'bg-primary/5 border-primary/20' 
                  : 'bg-muted/30 border-transparent hover:bg-muted/50'
              }`}
              onClick={() => onAccountToggle(account.id)}
            >
              <div className="flex-shrink-0">
                {isSelected ? (
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                ) : (
                  <Circle className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              
              <Avatar className="h-10 w-10">
                <div className="w-full h-full bg-gradient-to-br from-blue-400 to-green-400 flex items-center justify-center text-white text-sm font-semibold">
                  {account.username.slice(0, 2).toUpperCase()}
                </div>
              </Avatar>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-semibold truncate">{account.fullName}</h4>
                  <Badge variant="outline" className="text-xs">
                    {account.followers >= 1000000 
                      ? `${(account.followers / 1000000).toFixed(1)}M`
                      : account.followers >= 1000
                      ? `${(account.followers / 1000).toFixed(1)}K`
                      : account.followers.toLocaleString()
                    }
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-muted-foreground truncate">
                    @{account.username}
                  </p>
                  <Badge variant="secondary" className="text-xs">
                    {account.category}
                  </Badge>
                </div>
              </div>

              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={(e) => {
                  e.stopPropagation()
                  window.open(account.url, '_blank')
                }}
              >
                <ExternalLink className="h-3 w-3" />
              </Button>
            </div>
          )
        })}
      </div>

      {filteredAccounts.length === 0 && searchQuery && (
        <div className="text-center py-4">
          <p className="text-sm text-muted-foreground">
            No accounts match "{searchQuery}"
          </p>
        </div>
      )}
    </div>
  )
} 